import { FileText, MessageSquare, Rss } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";

import { Skeleton } from "@/components/ui/skeleton";
import { useBacheca } from "@/hooks/useBacheca";
import { useDocumenti } from "@/hooks/useDocumenti";
import { useFeed } from "@/hooks/useFeed";

const MAX_PER_COL = 3;

function formatRel(iso: string | null): string {
  if (!iso) return "—";
  const now = Date.now();
  const t = new Date(iso).getTime();
  const min = Math.floor((now - t) / 60000);
  if (min < 1) return "ora";
  if (min < 60) return `${min}min fa`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h fa`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}g fa`;
  return new Date(iso).toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "short",
  });
}

export function WidgetRecenti() {
  const bacheca = useBacheca();
  const documenti = useDocumenti();
  const feed = useFeed({ limit: 5 });

  return (
    <div className="grid gap-4 md:grid-cols-3">
      <Colonna
        icon={MessageSquare}
        titolo="Bacheca"
        linkBase="/bacheca"
        loading={bacheca.isLoading}
        items={(bacheca.data ?? [])
          .filter((v) => v.is_visibile_cliente)
          .sort(
            (a, b) =>
              new Date(b.ultimo_messaggio_il ?? b.updated_at).getTime() -
              new Date(a.ultimo_messaggio_il ?? a.updated_at).getTime()
          )
          .slice(0, MAX_PER_COL)
          .map((v) => ({
            id: v.id,
            label: v.titolo,
            iso: v.ultimo_messaggio_il ?? v.updated_at,
            href: `/bacheca?voce=${v.id}`,
          }))}
      />
      <Colonna
        icon={FileText}
        titolo="Documenti"
        linkBase="/documenti"
        loading={documenti.isLoading}
        items={(documenti.data ?? [])
          .sort(
            (a, b) =>
              new Date(b.caricato_il).getTime() - new Date(a.caricato_il).getTime()
          )
          .slice(0, MAX_PER_COL)
          .map((d) => ({
            id: d.id,
            label: d.nome,
            iso: d.caricato_il,
            href: `/documenti`,
          }))}
      />
      <Colonna
        icon={Rss}
        titolo="Feed"
        linkBase="/attivita"
        loading={feed.isLoading}
        items={(feed.data ?? [])
          .filter((f) => f.pubblicato_il)
          .slice(0, MAX_PER_COL)
          .map((f) => ({
            id: f.id,
            label: f.titolo_pubblicato ?? f.git_sha.substring(0, 7),
            iso: f.pubblicato_il,
            href: `/attivita`,
          }))}
      />
    </div>
  );
}

function Colonna({
  icon: Icon,
  titolo,
  linkBase,
  loading,
  items,
}: {
  icon: LucideIcon;
  titolo: string;
  linkBase: string;
  loading: boolean;
  items: { id: string; label: string; iso: string | null; href: string }[];
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-xs font-medium text-muted-foreground inline-flex items-center gap-1.5">
          <Icon className="size-3" />
          {titolo}
        </div>
        <Link
          to={linkBase}
          className="text-[10px] text-muted-foreground hover:text-foreground"
        >
          vedi tutti
        </Link>
      </div>
      {loading ? (
        <Skeleton className="h-12 w-full" />
      ) : items.length === 0 ? (
        <p className="text-xs text-muted-foreground/60 italic">nessuno</p>
      ) : (
        <ul className="space-y-1">
          {items.map((it) => (
            <li key={it.id}>
              <Link
                to={it.href}
                className="block text-xs hover:bg-muted/30 rounded px-2 py-1 -mx-2"
              >
                <p className="truncate font-medium text-foreground">{it.label}</p>
                <p className="text-[10px] text-muted-foreground">
                  {formatRel(it.iso)}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
